'use client'

import { Edit, MoreHorizontal, Pin, PinOff, Trash2 } from 'lucide-react'
import * as React from 'react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'

interface PostOptionsDropdownProps {
  showPin?: boolean
  showUnpin?: boolean
  showEdit?: boolean
  showDelete?: boolean
  isDeleting?: boolean
  onPin?: () => void
  onUnpin?: () => void
  onEdit?: () => void
  onDelete?: () => void
  open?: boolean
  onOpenChange?: (open: boolean) => void
  triggerClassName?: string
  contentClassName?: string
  iconClassName?: string
}

export function PostOptionsDropdown({
  showPin = false,
  showUnpin = false,
  showEdit = false,
  showDelete = false,
  isDeleting = false,
  onPin,
  onUnpin,
  onEdit,
  onDelete,
  open,
  onOpenChange,
  triggerClassName,
  contentClassName,
  iconClassName = 'h-5 w-5',
}: PostOptionsDropdownProps) {
  const showMenu = showPin || showUnpin || showEdit || showDelete
  if (!showMenu) return null

  const closeMenu = () => onOpenChange?.(false)

  return (
    <DropdownMenu open={open} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className={triggerClassName ?? 'text-gray-500'} aria-label="Post options">
          <MoreHorizontal className={iconClassName} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={5} className={contentClassName}>
        {showPin && onPin && (
          <DropdownMenuItem
            onClick={() => {
              onPin()
              closeMenu()
            }}
            onSelect={event => event.preventDefault()}
          >
            <Pin className="w-4 h-4 mr-2" />
            Ghim bài viết
          </DropdownMenuItem>
        )}
        {showUnpin && onUnpin && (
          <DropdownMenuItem
            onClick={() => {
              onUnpin()
              closeMenu()
            }}
            onSelect={event => event.preventDefault()}
          >
            <PinOff className="w-4 h-4 mr-2" />
            Bỏ ghim
          </DropdownMenuItem>
        )}
        {showEdit && onEdit && (
          <DropdownMenuItem
            onClick={() => {
              onEdit()
              closeMenu()
            }}
            onSelect={event => event.preventDefault()}
          >
            <Edit className="w-4 h-4 mr-2" />
            Chỉnh sửa
          </DropdownMenuItem>
        )}
        {showDelete && onDelete && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <DropdownMenuItem
                onSelect={event => event.preventDefault()}
                className="text-red-600 focus:text-red-600"
                disabled={isDeleting}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {isDeleting ? 'Đang xóa...' : 'Xóa'}
              </DropdownMenuItem>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Xóa bài viết</AlertDialogTitle>
                <AlertDialogDescription>
                  Bạn có chắc chắn muốn xóa bài viết này? Hành động này không thể hoàn tác.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Hủy</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() => {
                    onDelete()
                    closeMenu()
                  }}
                >
                  Xóa
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
